import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { authFetch, authJsonFetch } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Textarea } from '../components/ui/textarea';
import { DebouncedTextarea } from '../components/ui/debounced-textarea';
import { Separator } from '../components/ui/separator';
import { 
  Plus, 
  Search, 
  FolderOpen, 
  Edit, 
  Trash2,
  CalendarIcon,
  Building2,
  CheckCircle,
  Circle,
  Clock,
  ArrowUp,
  ArrowDown,
  FileText,
  Play,
  SkipForward,
  Loader2,
  Calendar,
  Truck,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  StickyNote,
  Save,
  History,
  FileInput,
  X,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
  MilestoneTimeline,
  statuses,
  milestoneStatuses,
  workflowTypes,
  workflowCategories,
  overdueDaysOptions
} from '../components/workflow';
import { HelpButton } from '../components/help';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const WorkflowsPage = () => {
  const { isBarbiePink } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('workflows');
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [photoDateFilter, setPhotoDateFilter] = useState('all'); // 'all', 'before', 'after'
  const [categoryFilter, setCategoryFilter] = useState('all'); // Category filter for workflows
  const [overdueDaysFilter, setOverdueDaysFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [hideCompleted, setHideCompleted] = useState(true);  // Default: hide completed workflows
  const [hideAfter8Weeks, setHideAfter8Weeks] = useState(true);  // Default: hide workflows starting after 8 weeks
  const [updatingMilestone, setUpdatingMilestone] = useState(null);
  const [filterWorkflowId, setFilterWorkflowId] = useState(null); // Filter to show a specific workflow
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalWorkflows, setTotalWorkflows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Handle filterWorkflowId from navigation state - auto-open the workflow detail dialog
  useEffect(() => {
    if (location.state?.filterWorkflowId) {
      const workflowId = location.state.filterWorkflowId;
      setFilterWorkflowId(workflowId);
      
      // Auto-open the workflow detail dialog
      const fetchAndOpenWorkflow = async () => {
        setDetailLoading(true);
        setDetailDialogOpen(true);
        try {
          const [workflowRes, auditsRes] = await Promise.all([
            authFetch(`${API}/workflows/${workflowId}`),
            authFetch(`${API}/workflows/${workflowId}/audits`)
          ]);
          
          if (workflowRes.ok) {
            const data = await workflowRes.json();
            setSelectedWorkflow(data);
            setWorkflowNotes(data.notes || '');
            
            // Initialize milestone notes
            const msNotes = {};
            (data.milestones || []).forEach(ms => {
              msNotes[ms.milestone_id] = ms.notes || '';
            });
            setMilestoneNotes(msNotes);
          }
          
          if (auditsRes.ok) {
            setAuditHistory(await auditsRes.json());
          }
        } catch (err) {
          toast.error('Failed to load workflow details');
        } finally {
          setDetailLoading(false);
        }
      };
      
      fetchAndOpenWorkflow();
      
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Workflow dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    org_id: '',
    workflow_type: '',
    category: 'production',
    status: 'pending',
    start_date: '',
    photography_date: '',
    shoot_date: '',
    dispatch_date: '',
    notes: '',
    year: new Date().getFullYear(),
    milestones: [],
    template_id: ''
  });
  
  // PrePhotoDay photography date prompt
  const [photoDatePromptOpen, setPhotoDatePromptOpen] = useState(false);
  const [pendingPhotoDate, setPendingPhotoDate] = useState('');

  // Workflow detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Notes editing state
  const [workflowNotes, setWorkflowNotes] = useState('');
  const [milestoneNotes, setMilestoneNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);

  // Milestone completion dialog state
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [milestoneWorkflowId, setMilestoneWorkflowId] = useState(null);
  const [milestoneDialogNotes, setMilestoneDialogNotes] = useState('');
  const [milestoneAuditLogs, setMilestoneAuditLogs] = useState([]);
  const [milestoneDialogLoading, setMilestoneDialogLoading] = useState(false);

  // Template dialog
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'production',
    milestones: []
  });

  // Milestone form
  const [milestoneForm, setMilestoneForm] = useState({
    name: '',
    description: '',
    due_days: 0
  });

  // Insert template state (for existing workflows)
  const [insertTemplateId, setInsertTemplateId] = useState('none');
  const [insertAfterMilestone, setInsertAfterMilestone] = useState('start');
  const [insertDaysAfter, setInsertDaysAfter] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (overdueDaysFilter && overdueDaysFilter !== 'all') params.append('overdue_days', overdueDaysFilter);
      if (yearFilter && yearFilter !== 'all') params.append('year', yearFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('page', currentPage);
      params.append('limit', pageSize);

      // Promise.allSettled so transient failures on the non-critical
      // orgs/years/templates calls don't trigger a misleading
      // "Failed to load data" toast when the workflows themselves loaded fine.
      const [workflowsResult, orgsResult, yearsResult, templatesResult] = await Promise.allSettled([
        authFetch(`${API}/workflows?${params}`),
        authFetch(`${API}/organizations?limit=1000`),
        authFetch(`${API}/workflow-milestone-years`),
        authFetch(`${API}/workflow-templates`),
      ]);

      // CRITICAL: workflows. Toast only if this fails.
      let criticalFailed = false;
      let workflowsRes = null;
      if (workflowsResult.status === 'fulfilled' && workflowsResult.value.ok) {
        workflowsRes = workflowsResult.value;
      } else {
        criticalFailed = true;
        console.error('Workflows fetch failed:', workflowsResult.status === 'rejected' ? workflowsResult.reason : `HTTP ${workflowsResult.value.status}`);
      }

      if (workflowsRes) {
        const result = await workflowsRes.json();
        let data = result.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Client-side filter for specific workflow ID (from "View in Workflows" link)
        // When filtering for a specific workflow, bypass all other filters
        if (filterWorkflowId) {
          data = data.filter(w => w.workflow_id === filterWorkflowId);
        } else {
          // Only apply other filters if not filtering for a specific workflow
          
          // Client-side filter for hide completed
          if (hideCompleted) {
            data = data.filter(w => w.status !== 'completed');
          }
          
          // Client-side filter for Before/After Photography Date
          if (photoDateFilter === 'before') {
            data = data.filter(w => {
              const photoDate = w.photography_date || w.shoot_date;
              if (!photoDate) return false; // Hide workflows without a date
              const workflowDate = new Date(photoDate);
              workflowDate.setHours(0, 0, 0, 0);
              return workflowDate < today;
            });
          } else if (photoDateFilter === 'after') {
            data = data.filter(w => {
              const photoDate = w.photography_date || w.shoot_date;
              if (!photoDate) return true; // Show workflows without a date as "upcoming"
              const workflowDate = new Date(photoDate);
              workflowDate.setHours(0, 0, 0, 0);
              return workflowDate >= today;
            });
          }
          
          // Client-side filter for hide after 8 weeks (workflows starting after 56 days)
          if (hideAfter8Weeks) {
            const eightWeeksFromNow = new Date(today.getTime() + (56 * 24 * 60 * 60 * 1000));
            data = data.filter(w => {
              const photoDate = w.photography_date || w.shoot_date;
              if (!photoDate) return true; // Show workflows without a date
              const workflowDate = new Date(photoDate);
              return workflowDate <= eightWeeksFromNow;
            });
          }
        }
        
        setWorkflows(data);
        setTotalWorkflows(hideCompleted || hideAfter8Weeks || photoDateFilter !== 'all' || filterWorkflowId ? data.length : (result.total || 0));
        setTotalPages(hideCompleted || hideAfter8Weeks || photoDateFilter !== 'all' || filterWorkflowId ? Math.ceil(data.length / pageSize) : (result.total_pages || 1));
      }
      // Non-critical fetches: silently degrade
      if (orgsResult.status === 'fulfilled' && orgsResult.value.ok) {
        try {
          const orgResult = await orgsResult.value.json();
          setOrganizations(orgResult.data || orgResult);
        } catch (_e) { /* noop */ }
      }
      if (yearsResult.status === 'fulfilled' && yearsResult.value.ok) {
        try { setYears(await yearsResult.value.json()); } catch (_e) { /* noop */ }
      }
      if (templatesResult.status === 'fulfilled' && templatesResult.value.ok) {
        try { setTemplates(await templatesResult.value.json()); } catch (_e) { /* noop */ }
      }
      if (criticalFailed) {
        toast.error('Failed to load workflows');
      }
    } catch (err) {
      console.error('Unexpected error in fetchData:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [photoDateFilter, categoryFilter, overdueDaysFilter, yearFilter, hideCompleted, hideAfter8Weeks, debouncedSearch, currentPage, pageSize, filterWorkflowId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [photoDateFilter, categoryFilter, overdueDaysFilter, yearFilter, hideCompleted, hideAfter8Weeks, pageSize, filterWorkflowId]);

  const openWorkflowDetail = async (workflow) => {
    setDetailLoading(true);
    setDetailDialogOpen(true);
    try {
      const [workflowRes, auditsRes] = await Promise.all([
        authFetch(`${API}/workflows/${workflow.workflow_id}`),
        authFetch(`${API}/workflows/${workflow.workflow_id}/audits`)
      ]);
      
      if (workflowRes.ok) {
        const data = await workflowRes.json();
        setSelectedWorkflow(data);
        setWorkflowNotes(data.notes || '');
        
        // Initialize milestone notes
        const msNotes = {};
        (data.milestones || []).forEach(ms => {
          msNotes[ms.milestone_id] = ms.notes || '';
        });
        setMilestoneNotes(msNotes);
      }
      
      if (auditsRes.ok) {
        setAuditHistory(await auditsRes.json());
      }
    } catch (err) {
      toast.error('Failed to load workflow details');
    } finally {
      setDetailLoading(false);
    }
  };

  const saveWorkflowNotes = async () => {
    if (!selectedWorkflow) return;
    setSavingNotes('workflow');
    try {
      const response = await authJsonFetch(`${API}/workflows/${selectedWorkflow.workflow_id}/notes`, {
        method: 'PUT',
        body: JSON.stringify({ notes: workflowNotes })
      });
      
      if (response.ok) {
        toast.success('Workflow notes saved');
        // Refresh audit history
        const auditsRes = await authFetch(`${API}/workflows/${selectedWorkflow.workflow_id}/audits`);
        if (auditsRes.ok) setAuditHistory(await auditsRes.json());
      } else {
        toast.error('Failed to save notes');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setSavingNotes(null);
    }
  };

  const saveMilestoneNotes = async (milestoneId) => {
    if (!selectedWorkflow) return;
    setSavingNotes(milestoneId);
    try {
      const response = await authJsonFetch(
        `${API}/workflows/${selectedWorkflow.workflow_id}/milestones/${milestoneId}/notes`,
        {
          method: 'PUT',
          body: JSON.stringify({ notes: milestoneNotes[milestoneId] || '' })
        }
      );
      
      if (response.ok) {
        toast.success('Milestone notes saved');
        // Refresh audit history
        const auditsRes = await authFetch(`${API}/workflows/${selectedWorkflow.workflow_id}/audits`);
        if (auditsRes.ok) setAuditHistory(await auditsRes.json());
      } else {
        toast.error('Failed to save notes');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setSavingNotes(null);
    }
  };

  const updateWorkflowDates = async (field, value) => {
    if (!selectedWorkflow) return;
    
    try {
      const response = await authJsonFetch(
        `${API}/workflows/${selectedWorkflow.workflow_id}/dates`,
        {
          method: 'PUT',
          body: JSON.stringify({ [field]: value })
        }
      );
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedWorkflow(updated);
        toast.success(`${field === 'photography_date' ? 'Photography' : 'Dispatch'} date updated`);
        // Refresh audit history
        const auditsRes = await authFetch(`${API}/workflows/${selectedWorkflow.workflow_id}/audits`);
        if (auditsRes.ok) setAuditHistory(await auditsRes.json());
        // Refresh main list
        fetchData();
      } else {
        toast.error('Failed to update date');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const openMilestoneDialog = async (workflowId, milestone) => {
    setMilestoneWorkflowId(workflowId);
    setSelectedMilestone(milestone);
    setMilestoneDialogNotes(milestone.notes || '');
    setMilestoneDialogOpen(true);
    
    // Fetch audit logs for this milestone
    try {
      const response = await authFetch(`${API}/workflows/${workflowId}/audits`);
      if (response.ok) {
        const allAudits = await response.json();
        // Filter to only this milestone's logs
        const msLogs = allAudits.filter(a => a.milestone_id === milestone.milestone_id);
        setMilestoneAuditLogs(msLogs);
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    }
  };

  const confirmMilestoneToggle = async () => {
    if (!selectedMilestone || !milestoneWorkflowId) return;
    
    setMilestoneDialogLoading(true);
    const currentStatus = selectedMilestone.status;
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    
    try {
      // First save notes if changed
      if (milestoneDialogNotes !== (selectedMilestone.notes || '')) {
        await authJsonFetch(`${API}/workflows/${milestoneWorkflowId}/milestones/${selectedMilestone.milestone_id}/notes`, {
          method: 'PUT',
          body: JSON.stringify({ notes: milestoneDialogNotes })
        });
      }
      
      // Then toggle status
      const response = await authFetch(
        `${API}/workflows/${milestoneWorkflowId}/milestones/${selectedMilestone.milestone_id}/status?status=${newStatus}`,
        { method: 'PUT' }
      );

      if (response.ok) {
        toast.success(`Milestone marked as ${newStatus}`);
        setMilestoneDialogOpen(false);
        fetchData();
        
        // Refresh detail dialog if open
        if (selectedWorkflow && selectedWorkflow.workflow_id === milestoneWorkflowId) {
          const refreshRes = await authFetch(`${API}/workflows/${milestoneWorkflowId}`);
          if (refreshRes.ok) {
            const updatedWorkflow = await refreshRes.json();
            setSelectedWorkflow(updatedWorkflow);
            // Update milestone notes state
            const notesMap = {};
            updatedWorkflow.milestones?.forEach(ms => {
              notesMap[ms.milestone_id] = ms.notes || '';
            });
            setMilestoneNotes(notesMap);
          }
        }
      } else {
        toast.error('Failed to update milestone');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setMilestoneDialogLoading(false);
    }
  };

  // Legacy function for direct toggle (now opens dialog)
  const toggleMilestoneStatus = async (workflowId, milestoneId, currentStatus, milestone) => {
    // Find the full milestone object
    let ms = milestone;
    if (!ms) {
      // Try to find from selectedWorkflow or workflows list
      const wf = selectedWorkflow?.workflow_id === workflowId 
        ? selectedWorkflow 
        : workflows.find(w => w.workflow_id === workflowId);
      ms = wf?.milestones?.find(m => m.milestone_id === milestoneId);
    }
    
    if (ms) {
      openMilestoneDialog(workflowId, { ...ms, status: currentStatus });
    } else {
      // Fallback: Open dialog with basic info
      openMilestoneDialog(workflowId, { 
        milestone_id: milestoneId, 
        status: currentStatus,
        name: 'Milestone',
        notes: ''
      });
    }
  };

  // Handle category change - special logic for PrePhotoDay
  const handleCategoryChange = async (newCategory) => {
    setFormData(prev => ({ ...prev, category: newCategory }));
    
    // Special handling for PrePhotoDay category
    if (newCategory === 'prephotoday' && formData.org_id) {
      try {
        // Fetch organisation's photography dates
        const response = await authFetch(`${API}/organizations/${formData.org_id}/photography-dates`);
        
        if (response.ok) {
          const data = await response.json();
          const photographyDates = data.photography_dates || {};
          const year = formData.year || new Date().getFullYear();
          
          // Check if photography date exists for the selected year
          if (photographyDates[year.toString()]) {
            const photoDate = photographyDates[year.toString()];
            setFormData(prev => ({
              ...prev,
              category: newCategory,
              photography_date: photoDate,
              shoot_date: photoDate,
              // PrePhotoDay milestones: Confirmation Letter (-42), Photography Date (0), Dispatch Date (+42)
              milestones: prev.milestones?.length > 0 ? prev.milestones : [
                {
                  milestone_id: `ms_${Date.now()}_1`,
                  name: 'Confirmation Letter',
                  description: 'Send confirmation letter to organisation',
                  order: 1,
                  due_days: -42,
                  status: 'pending'
                },
                {
                  milestone_id: `ms_${Date.now()}_2`,
                  name: 'Photography Date',
                  description: 'Photo session day',
                  order: 2,
                  due_days: 0,
                  status: 'pending'
                },
                {
                  milestone_id: `ms_${Date.now()}_3`,
                  name: 'Dispatch Date',
                  description: 'Dispatch photos to organisation',
                  order: 3,
                  due_days: 42,
                  status: 'pending'
                }
              ]
            }));
            toast.success(`Photography date loaded: ${new Date(photoDate).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}`);
          } else {
            // No photography date found for this year - prompt user
            setPhotoDatePromptOpen(true);
            setPendingPhotoDate('');
          }
        }
      } catch (err) {
        console.error('Failed to fetch photography dates:', err);
        // Still prompt for date
        setPhotoDatePromptOpen(true);
        setPendingPhotoDate('');
      }
    }
    
    // Handle Production category - add Photography Date as first milestone
    if (newCategory === 'production' && formData.milestones?.length === 0) {
      setFormData(prev => ({
        ...prev,
        category: newCategory,
        milestones: [
          {
            milestone_id: `ms_${Date.now()}`,
            name: 'Photography Date',
            description: 'Photo session day',
            order: 1,
            due_days: 0,
            status: 'pending'
          }
        ]
      }));
    }
  };

  // Handle saving photography date when prompted
  const handleSavePhotoDate = async () => {
    if (!pendingPhotoDate || !formData.org_id) {
      toast.error('Please enter a valid date');
      return;
    }
    
    try {
      const year = formData.year || new Date().getFullYear();
      
      // Save to organisation
      const response = await authJsonFetch(`${API}/organizations/${formData.org_id}/photography-date`, {
        method: 'PUT',
        body: JSON.stringify({ year: year.toString(), date: pendingPhotoDate })
      });
      
      if (response.ok) {
        setFormData(prev => ({
          ...prev,
          photography_date: pendingPhotoDate,
          shoot_date: pendingPhotoDate,
          // PrePhotoDay milestones: Confirmation Letter (-42), Photography Date (0), Dispatch Date (+42)
          milestones: prev.milestones?.length > 0 ? prev.milestones : [
            {
              milestone_id: `ms_${Date.now()}_1`,
              name: 'Confirmation Letter',
              description: 'Send confirmation letter to organisation',
              order: 1,
              due_days: -42,
              status: 'pending'
            },
            {
              milestone_id: `ms_${Date.now()}_2`,
              name: 'Photography Date',
              description: 'Photo session day',
              order: 2,
              due_days: 0,
              status: 'pending'
            },
            {
              milestone_id: `ms_${Date.now()}_3`,
              name: 'Dispatch Date',
              description: 'Dispatch photos to organisation',
              order: 3,
              due_days: 42,
              status: 'pending'
            }
          ]
        }));
        setPhotoDatePromptOpen(false);
        toast.success('Photography date saved');
      } else {
        toast.error('Failed to save photography date');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingWorkflow 
        ? `${API}/workflows/${editingWorkflow.workflow_id}`
        : `${API}/workflows`;
      
      const response = await authJsonFetch(url, {
        method: editingWorkflow ? 'PUT' : 'POST',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingWorkflow ? 'Workflow updated' : 'Workflow created');
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        toast.error('Failed to save workflow');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const openDeleteDialog = (workflow) => {
    setWorkflowToDelete(workflow);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await authFetch(`${API}/workflows/${workflowToDelete.workflow_id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Workflow deleted - notification email sent');
        fetchData();
      } else {
        toast.error('Failed to delete workflow');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const addMilestoneToWorkflow = () => {
    if (!milestoneForm.name) {
      toast.error('Milestone name is required');
      return;
    }
    
    const newMilestone = {
      milestone_id: `ms_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: milestoneForm.name,
      description: milestoneForm.description,
      due_days: parseInt(milestoneForm.due_days) || 0,
      order: formData.milestones.length,
      status: 'pending',
      notes: ''
    };
    
    setFormData({
      ...formData,
      milestones: [...formData.milestones, newMilestone]
    });
    setMilestoneForm({ name: '', description: '', due_days: 0 });
  };

  const updateMilestoneInForm = (index, field, value) => {
    const updated = [...formData.milestones];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, milestones: updated });
  };

  const removeMilestoneFromForm = (index) => {
    const updated = formData.milestones.filter((_, i) => i !== index);
    updated.forEach((ms, i) => { ms.order = i; });
    setFormData({ ...formData, milestones: updated });
  };

  const moveMilestone = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.milestones.length) return;
    
    const updated = [...formData.milestones];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((ms, i) => { ms.order = i; });
    setFormData({ ...formData, milestones: updated });
  };

  // Insert template milestones into existing workflow
  const handleInsertTemplate = () => {
    if (!insertTemplateId || insertTemplateId === 'none') {
      toast.error('Please select a template');
      return;
    }

    const template = templates.find(t => t.template_id === insertTemplateId);
    if (!template || !template.milestones || template.milestones.length === 0) {
      toast.error('Template has no milestones');
      return;
    }

    // Sort template milestones by their due_days to get the correct order
    const sortedTemplateMilestones = [...template.milestones].sort((a, b) => (a.due_days || 0) - (b.due_days || 0));
    
    // Find the base due_days from the selected insertion point
    let baseDueDays = 0;
    
    if (insertAfterMilestone !== 'start') {
      // Find the milestone we're inserting after
      const afterMilestone = formData.milestones.find(
        ms => ms.milestone_id === insertAfterMilestone
      );
      if (afterMilestone) {
        // Get the due_days of the selected milestone + user specified days
        const selectedDueDays = parseInt(afterMilestone.due_days) || 0;
        baseDueDays = selectedDueDays + (parseInt(insertDaysAfter) || 1);
      }
    } else {
      // Inserting at start - use the first milestone's due_days as reference, or 0
      if (formData.milestones.length > 0) {
        const firstMilestone = formData.milestones.reduce((min, ms) => 
          (parseInt(ms.due_days) || 0) < (parseInt(min.due_days) || 0) ? ms : min
        );
        // Start before the first milestone by the specified days
        baseDueDays = (parseInt(firstMilestone.due_days) || 0) - (parseInt(insertDaysAfter) || 7);
      }
    }

    // Calculate the offset: template milestones are relative to each other
    // We shift them so the first template milestone starts at the base due_days
    const templateMinDays = Math.min(...sortedTemplateMilestones.map(ms => ms.due_days || 0));
    const offset = baseDueDays - templateMinDays;

    // Create new milestones with calculated due_days
    const newMilestones = sortedTemplateMilestones.map((ms, idx) => ({
      milestone_id: `ms_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`,
      name: ms.name,
      description: ms.description || '',
      due_days: (parseInt(ms.due_days) || 0) + offset,
      status: 'pending',
      notes: ''
    }));

    let updatedMilestones = [...formData.milestones];
    
    if (insertAfterMilestone === 'start') {
      // Insert at the beginning
      updatedMilestones = [...newMilestones, ...updatedMilestones];
    } else {
      // Find the index of the milestone to insert after
      const insertIndex = updatedMilestones.findIndex(
        ms => ms.milestone_id === insertAfterMilestone
      );
      
      if (insertIndex !== -1) {
        // Insert after the found milestone
        updatedMilestones = [
          ...updatedMilestones.slice(0, insertIndex + 1),
          ...newMilestones,
          ...updatedMilestones.slice(insertIndex + 1)
        ];
      } else {
        // Fallback: append at end
        updatedMilestones = [...updatedMilestones, ...newMilestones];
      }
    }

    // Re-assign order numbers
    updatedMilestones.forEach((ms, i) => { ms.order = i; });

    setFormData({ ...formData, milestones: updatedMilestones });
    
    // Reset insert template state
    setInsertTemplateId('none');
    setInsertAfterMilestone('start');
    setInsertDaysAfter(1);
    
    toast.success(`Inserted ${newMilestones.length} milestones from "${template.name}" (${insertDaysAfter} days after selected milestone)`);
  };

  const applyTemplate = (templateId) => {
    if (templateId === 'none') {
      setFormData({ ...formData, template_id: '', milestones: [] });
      return;
    }
    const template = templates.find(t => t.template_id === templateId);
    if (template) {
      const milestones = (template.milestones || []).map((ms, idx) => ({
        ...ms,
        milestone_id: `ms_${Date.now()}_${idx}`,
        status: 'pending',
        order: idx
      }));
      setFormData({ ...formData, template_id: templateId, milestones });
      toast.success('Template applied');
    }
  };

  // Template CRUD
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      const url = editingTemplate 
        ? `${API}/workflow-templates/${editingTemplate.template_id}`
        : `${API}/workflow-templates`;
      
      const response = await authJsonFetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setTemplateDialogOpen(false);
        resetTemplateForm();
        fetchData();
      } else {
        toast.error('Failed to save template');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const response = await authFetch(`${API}/workflow-templates/${templateId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchData();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      // Create a copy of the template with a new name
      const duplicatedTemplate = {
        name: `${template.name} (Copy)`,
        description: template.description || '',
        category: template.category || 'production',
        milestones: (template.milestones || []).map(ms => ({
          name: ms.name,
          description: ms.description || '',
          due_days: ms.due_days || 0
        }))
      };

      const response = await authJsonFetch(`${API}/workflow-templates`, {
        method: 'POST',
        body: JSON.stringify(duplicatedTemplate)
      });

      if (response.ok) {
        toast.success('Template duplicated successfully');
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to duplicate template');
      }
    } catch (err) {
      toast.error('An error occurred while duplicating');
    }
  };

  const addMilestoneToTemplate = () => {
    if (!milestoneForm.name) {
      toast.error('Milestone name is required');
      return;
    }
    
    const newMilestone = {
      milestone_id: `ms_tpl_${Date.now()}`,
      name: milestoneForm.name,
      description: milestoneForm.description,
      due_days: parseInt(milestoneForm.due_days) || 0,
      order: templateForm.milestones.length
    };
    
    setTemplateForm({
      ...templateForm,
      milestones: [...templateForm.milestones, newMilestone]
    });
    setMilestoneForm({ name: '', description: '', due_days: 0 });
  };

  const updateMilestoneInTemplate = (index, field, value) => {
    const updated = [...templateForm.milestones];
    updated[index] = { ...updated[index], [field]: value };
    setTemplateForm({ ...templateForm, milestones: updated });
  };

  const removeMilestoneFromTemplate = (index) => {
    const updated = templateForm.milestones.filter((_, i) => i !== index);
    updated.forEach((ms, i) => { ms.order = i; });
    setTemplateForm({ ...templateForm, milestones: updated });
  };

  const moveTemplateMilestone = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= templateForm.milestones.length) return;
    
    const updated = [...templateForm.milestones];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((ms, i) => { ms.order = i; });
    setTemplateForm({ ...templateForm, milestones: updated });
  };

  const openEditDialog = (workflow) => {
    setEditingWorkflow(workflow);
    setFormData({
      name: workflow.name,
      org_id: workflow.org_id,
      workflow_type: workflow.workflow_type,
      category: workflow.category || 'production',
      status: workflow.status,
      start_date: workflow.start_date || '',
      photography_date: workflow.photography_date || workflow.shoot_date || '',
      shoot_date: workflow.shoot_date || '',
      dispatch_date: workflow.dispatch_date || '',
      notes: workflow.notes || '',
      year: workflow.year,
      milestones: workflow.milestones || [],
      template_id: workflow.template_id || ''
    });
    setDialogOpen(true);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      milestones: template.milestones || []
    });
    setTemplateDialogOpen(true);
  };

  const resetForm = () => {
    setEditingWorkflow(null);
    setFormData({
      name: '',
      org_id: '',
      workflow_type: '',
      category: 'production',
      status: 'pending',
      start_date: '',
      photography_date: '',
      shoot_date: '',
      dispatch_date: '',
      notes: '',
      year: new Date().getFullYear(),
      milestones: [],
      template_id: ''
    });
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      description: '',
      category: 'production',
      milestones: []
    });
  };

  // Generate default milestones based on category
  const getDefaultMilestonesForCategory = (category) => {
    if (category === 'production') {
      return [
        {
          milestone_id: `ms_${Date.now()}`,
          name: 'Photography Date',
          description: 'Photo session day',
          order: 1,
          due_days: 0,
          status: 'pending'
        }
      ];
    } else if (category === 'prephotoday') {
      return [
        {
          milestone_id: `ms_${Date.now()}_1`,
          name: 'Confirmation Letter',
          description: 'Send confirmation letter to organisation',
          order: 1,
          due_days: -42,
          status: 'pending'
        },
        {
          milestone_id: `ms_${Date.now()}_2`,
          name: 'Photography Date',
          description: 'Photo session day',
          order: 2,
          due_days: 0,
          status: 'pending'
        },
        {
          milestone_id: `ms_${Date.now()}_3`,
          name: 'Dispatch Date',
          description: 'Dispatch photos to organisation',
          order: 3,
          due_days: 42,
          status: 'pending'
        }
      ];
    }
    return [];
  };

  // Handle template category change
  const handleTemplateCategoryChange = (newCategory) => {
    const currentMilestones = templateForm.milestones;
    const defaultMilestones = getDefaultMilestonesForCategory(newCategory);
    
    // If no milestones, add defaults
    if (currentMilestones.length === 0 && defaultMilestones.length > 0) {
      setTemplateForm({
        ...templateForm,
        category: newCategory,
        milestones: defaultMilestones
      });
    } else {
      setTemplateForm({ ...templateForm, category: newCategory });
    }
  };

  const getOrgName = (orgId) => {
    const orgs = Array.isArray(organizations) ? organizations : [];
    const org = orgs.find(o => o.org_id === orgId);
    return org?.name || 'Unknown';
  };

  const getStatusStyle = (status) => {
    const s = statuses.find(st => st.value === status);
    return s?.color || statuses[0].color;
  };

  const calculateProgress = (milestones) => {
    if (!milestones || milestones.length === 0) return 0;
    const completed = milestones.filter(m => m.status === 'completed').length;
    return Math.round((completed / milestones.length) * 100);
  };

  const calculateDispatchDate = (shootDate) => {
    if (!shootDate) return null;
    const shoot = new Date(shootDate);
    shoot.setDate(shoot.getDate() + 42);
    return shoot.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney' });
  };

  return (
    <div data-testid="workflows-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Workflows</h1>
          <p className="text-muted-foreground mt-1">
            Track production and project lifecycles with milestones ({totalWorkflows} total)
          </p>
        </div>
        <HelpButton topic="workflows" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="workflows" data-testid="workflows-tab">
            <FolderOpen className="w-4 h-4 mr-2" />
            Workflows
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
        </TabsList>

        {/* Workflows Tab */}
        <TabsContent value="workflows" className="mt-6 space-y-6">
          <div className="flex justify-end">
            <Button 
              data-testid="add-workflow-btn"
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              New Workflow
            </Button>
          </div>

          {/* Filters */}
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="workflow-search-input"
                    placeholder="Search workflows..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={yearFilter} onValueChange={setYearFilter}>
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="All Years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {years.map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={photoDateFilter} onValueChange={setPhotoDateFilter}>
                  <SelectTrigger className="w-full sm:w-32" data-testid="photo-date-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="before">Before</SelectItem>
                    <SelectItem value="after">After</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={overdueDaysFilter} onValueChange={setOverdueDaysFilter}>
                  <SelectTrigger className="w-full sm:w-44" data-testid="overdue-filter">
                    <SelectValue placeholder="Overdue Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    {overdueDaysOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full sm:w-36" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {workflowCategories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger data-testid="workflow-page-size" className="w-full sm:w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 / page</SelectItem>
                    <SelectItem value="20">20 / page</SelectItem>
                    <SelectItem value="50">50 / page</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="hide-after-8weeks"
                      checked={hideAfter8Weeks}
                      onChange={(e) => setHideAfter8Weeks(e.target.checked)}
                      className="w-4 h-4 rounded"
                      data-testid="hide-after-8weeks-checkbox"
                    />
                    <label htmlFor="hide-after-8weeks" className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
                      Hide After 8 Weeks
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="hide-completed"
                      checked={hideCompleted}
                      onChange={(e) => setHideCompleted(e.target.checked)}
                      className="w-4 h-4 rounded"
                      data-testid="hide-completed-checkbox"
                    />
                    <label htmlFor="hide-completed" className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
                      Hide Completed
                    </label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filter indicator for specific workflow */}
          {filterWorkflowId && (
            <Card className={cn("border-blue-500 bg-blue-50/50 dark:bg-blue-950/20", isBarbiePink && "card-brutalist")}>
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <Search className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700 dark:text-blue-400">Showing specific workflow from Organizations</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setFilterWorkflowId(null)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear Filter
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Workflow List - Modern Card Design */}
          <div className="space-y-5">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : workflows.length === 0 ? (
              <Card className={cn(isBarbiePink && "card-brutalist")}>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No workflows found
                </CardContent>
              </Card>
            ) : (
              workflows.map((workflow) => {
                const progress = calculateProgress(workflow.milestones);
                const completedCount = workflow.milestones?.filter(m => m.status === 'completed').length || 0;
                const totalMilestones = workflow.milestones?.length || 0;
                const categoryInfo = workflowCategories.find(c => c.value === workflow.category) || workflowCategories[1]; // Default to production
                
                return (
                  <Card 
                    key={workflow.workflow_id} 
                    className={cn(
                      "group overflow-hidden transition-all duration-200 hover:shadow-lg border-l-4",
                      workflow.status === 'completed' ? "border-l-green-500" :
                      workflow.status === 'in_progress' ? "border-l-blue-500" :
                      "border-l-gray-300",
                      isBarbiePink && "card-brutalist"
                    )}
                    data-testid={`workflow-card-${workflow.workflow_id}`}
                  >
                    <CardContent className="p-0">
                      <div className="flex">
                        {/* Main Content */}
                        <div className="flex-1 p-6">
                          {/* Top Row: Title, Org, Status */}
                          <div className="flex items-start justify-between mb-4">
                            <div 
                              className="cursor-pointer flex-1"
                              onClick={() => openWorkflowDetail(workflow)}
                            >
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className="font-semibold text-lg hover:text-primary transition-colors">
                                  {workflow.name}
                                </h3>
                                <Badge className={cn("text-xs", categoryInfo.color)}>
                                  {categoryInfo.label}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <button 
                                  className="flex items-center gap-1.5 hover:text-primary transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/organizations', { state: { selectOrgId: workflow.org_id } });
                                  }}
                                  data-testid={`workflow-org-link-${workflow.workflow_id}`}
                                >
                                  <Building2 className="w-3.5 h-3.5" />
                                  <span className="hover:underline">{workflow.org_name || getOrgName(workflow.org_id)}</span>
                                </button>
                                <span className="text-muted-foreground/50">•</span>
                                <span>{workflow.year}</span>
                              </div>
                            </div>
                            <Badge className={cn("shrink-0", getStatusStyle(workflow.status))}>
                              {(workflow.status || 'pending').replace('_', ' ')}
                            </Badge>
                          </div>

                          {/* Middle Row: Dates & Progress */}
                          <div className="flex items-center gap-6 mb-4">
                            {/* Dates */}
                            <div className="flex gap-4 text-sm">
                              {(workflow.photography_date || workflow.shoot_date) ? (
                                <>
                                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400">
                                    <Calendar className="w-3.5 h-3.5" />
                                    <span className="font-medium">Photo:</span>
                                    <span>{new Date(workflow.photography_date || workflow.shoot_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' })}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400">
                                    <Truck className="w-3.5 h-3.5" />
                                    <span className="font-medium">Dispatch:</span>
                                    <span>{workflow.dispatch_date ? new Date(workflow.dispatch_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' }) : calculateDispatchDate(workflow.photography_date || workflow.shoot_date)}</span>
                                  </div>
                                </>
                              ) : (
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400 text-xs">
                                  <Calendar className="w-3.5 h-3.5" />
                                  <span>No photography date set</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Progress */}
                            <div className="flex-1 max-w-xs">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-muted-foreground">Progress</span>
                                <span className="font-semibold">{completedCount}/{totalMilestones}</span>
                              </div>
                              <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                <div 
                                  className={cn(
                                    "h-full transition-all duration-500 rounded-full",
                                    progress === 100 ? "bg-green-500" :
                                    progress > 50 ? "bg-blue-500" :
                                    progress > 0 ? "bg-amber-500" : "bg-gray-300"
                                  )}
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Bottom Row: Milestones Timeline */}
                          <div className="pt-3 border-t border-dashed">
                            <MilestoneTimeline 
                              workflow={workflow}
                              onMilestoneClick={toggleMilestoneStatus}
                              isUpdating={updatingMilestone !== null}
                            />
                          </div>
                        </div>

                        {/* Right Actions Panel */}
                        <div className="w-12 bg-muted/30 flex flex-col items-center justify-center gap-1 border-l opacity-0 group-hover:opacity-100 transition-opacity">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9"
                                  onClick={() => openWorkflowDetail(workflow)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="left">View Details</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9"
                                  onClick={() => openEditDialog(workflow)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="left">Edit</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9 text-destructive hover:text-destructive"
                                  onClick={() => openDeleteDialog(workflow)}
                                  data-testid={`delete-workflow-${workflow.workflow_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="left">Delete</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border rounded-lg bg-card">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalWorkflows)} of {totalWorkflows} workflows
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  data-testid="wf-first-page-btn"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  data-testid="wf-prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="wf-next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  data-testid="wf-last-page-btn"
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6 space-y-6">
          <div className="flex justify-end">
            <Button 
              data-testid="add-template-btn"
              onClick={() => { resetTemplateForm(); setTemplateDialogOpen(true); }}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              New Template
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.length === 0 ? (
              <Card className={cn("col-span-full", isBarbiePink && "card-brutalist")}>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No templates yet. Create your first template.
                </CardContent>
              </Card>
            ) : (
              templates.map((template) => (
                <Card key={template.template_id} className={cn(isBarbiePink && "card-brutalist")}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      {template.name}
                      <Badge variant="secondary">
                        {template.milestones?.length || 0} milestones
                      </Badge>
                    </CardTitle>
                    {template.description && (
                      <p className="text-sm text-muted-foreground">{template.description}</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {template.milestones?.length > 0 && (
                      <div className="space-y-1 mb-4">
                        {template.milestones.slice(0, 5).map((ms, idx) => (
                          <div key={ms.milestone_id || idx} className="flex items-center gap-2 text-sm">
                            <Circle className="w-3 h-3 text-muted-foreground" />
                            <span>{ms.name}</span>
                            <span className={cn(
                              "text-xs",
                              ms.due_days < 0 ? "text-orange-600" : "text-muted-foreground"
                            )}>
                              {ms.due_days > 0 ? `+${ms.due_days}d` : ms.due_days < 0 ? `${ms.due_days}d` : '0d'}
                            </span>
                          </div>
                        ))}
                        {template.milestones.length > 5 && (
                          <p className="text-xs text-muted-foreground">
                            +{template.milestones.length - 5} more...
                          </p>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditTemplate(template)}>
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDuplicateTemplate(template)}
                        data-testid={`duplicate-template-${template.template_id}`}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Duplicate
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-destructive"
                        onClick={() => handleDeleteTemplate(template.template_id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Workflow Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              {selectedWorkflow?.name || 'Workflow Details'}
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : selectedWorkflow && (
            <div className="space-y-6">
              {/* Status & Progress */}
              <div className="flex items-center justify-between">
                <Badge className={getStatusStyle(selectedWorkflow.status)}>
                  {(selectedWorkflow.status || 'pending').replace('_', ' ')}
                </Badge>
                <span className="text-2xl font-bold">
                  {calculateProgress(selectedWorkflow.milestones)}% Complete
                </span>
              </div>
              <Progress value={calculateProgress(selectedWorkflow.milestones)} className="h-3" />

              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Organization</p>
                  <p className="font-medium">{getOrgName(selectedWorkflow.org_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{(selectedWorkflow.workflow_type || 'other').replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Year</p>
                  <p className="font-medium">{selectedWorkflow.year}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Photography Date</Label>
                  <Input
                    type="date"
                    value={selectedWorkflow.photography_date || selectedWorkflow.shoot_date || ''}
                    onChange={(e) => updateWorkflowDates('photography_date', e.target.value)}
                    className="h-8 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Dispatch Date</Label>
                  <Input
                    type="date"
                    value={selectedWorkflow.dispatch_date || ''}
                    onChange={(e) => updateWorkflowDates('dispatch_date', e.target.value)}
                    className="h-8 mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedWorkflow.photography_date || selectedWorkflow.shoot_date 
                      ? `Auto: ${calculateDispatchDate(selectedWorkflow.photography_date || selectedWorkflow.shoot_date)}`
                      : 'Set photography date first'}
                  </p>
                </div>
              </div>

              {/* Workflow Notes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Workflow Notes
                  </Label>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={saveWorkflowNotes}
                    disabled={savingNotes === 'workflow'}
                  >
                    {savingNotes === 'workflow' ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Save className="w-3 h-3 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
                <Textarea
                  placeholder="Add notes about this workflow..."
                  value={workflowNotes}
                  onChange={(e) => setWorkflowNotes(e.target.value)}
                  className="min-h-[80px]"
                />
              </div>

              <Separator />

              {/* Milestone Timeline with Notes */}
              <div>
                <h3 className="font-semibold mb-4">
                  Milestones - Click circles to toggle completion
                </h3>
                <MilestoneTimeline 
                  workflow={selectedWorkflow}
                  onMilestoneClick={toggleMilestoneStatus}
                  isUpdating={updatingMilestone !== null}
                />
                
                {/* Individual Milestone Notes */}
                <div className="mt-6 space-y-4">
                  <h4 className="text-sm font-medium text-muted-foreground">Milestone Notes</h4>
                  {selectedWorkflow.milestones?.map((ms) => (
                    <div key={ms.milestone_id} className="p-3 border rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {ms.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Circle className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="font-medium text-sm">{ms.name}</span>
                          {ms.due_days !== undefined && (
                            <Badge variant="outline" className="text-xs">
                              {ms.due_days > 0 ? `+${ms.due_days}` : ms.due_days} days
                            </Badge>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => saveMilestoneNotes(ms.milestone_id)}
                          disabled={savingNotes === ms.milestone_id}
                        >
                          {savingNotes === ms.milestone_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Save className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                      <DebouncedTextarea
                        placeholder={`Notes for ${ms.name}...`}
                        value={milestoneNotes[ms.milestone_id] || ''}
                        onChange={(e) => setMilestoneNotes(prev => ({
                          ...prev,
                          [ms.milestone_id]: e.target.value
                        }))}
                        className="min-h-[60px] text-sm"
                        debounceMs={300}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Audit History */}
              {auditHistory.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Change History
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {auditHistory.slice(0, 30).map((audit, idx) => (
                        <div key={audit.audit_id || idx} className="text-sm flex items-start gap-2 p-2 bg-muted/50 rounded">
                          <Clock className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-muted-foreground text-xs">
                                {new Date(audit.changed_at).toLocaleString('en-AU', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  timeZone: 'Australia/Sydney'
                                })}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {audit.action.replace(/_/g, ' ')}
                              </Badge>
                              {audit.milestone_id && audit.milestone_id !== 'workflow' && (
                                <span className="text-xs text-muted-foreground">
                                  Milestone: {selectedWorkflow.milestones?.find(m => m.milestone_id === audit.milestone_id)?.name || audit.milestone_id}
                                </span>
                              )}
                            </div>
                            {audit.new_value?.status && (
                              <span className="text-xs">→ {audit.new_value.status}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
            <Button onClick={() => { setDetailDialogOpen(false); openEditDialog(selectedWorkflow); }}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Workflow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Workflow Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingWorkflow ? 'Edit Workflow' : 'New Workflow'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Workflow Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Organization *</Label>
                  <Select value={formData.org_id} onValueChange={(v) => setFormData({...formData, org_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map(org => (
                        <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={handleCategoryChange}>
                    <SelectTrigger data-testid="workflow-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {workflowCategories.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.category === 'prephotoday' && (
                    <p className="text-xs text-orange-600 mt-1">
                      Completion = Photography Date. Default milestone: -42 days
                    </p>
                  )}
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year *</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div>
                  <Label>Photography Date</Label>
                  <Input
                    type="date"
                    value={formData.photography_date || formData.shoot_date || ''}
                    onChange={(e) => setFormData({...formData, photography_date: e.target.value, shoot_date: e.target.value})}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Auto Dispatch = Photo + 42 days
                  </p>
                </div>
                <div>
                  <Label>Dispatch Date</Label>
                  <Input
                    type="date"
                    value={formData.dispatch_date || ''}
                    onChange={(e) => setFormData({...formData, dispatch_date: e.target.value})}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Override auto-calculated date
                  </p>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                />
              </div>

              <Separator />

              {/* Template Selection - For New Workflows */}
              {!editingWorkflow && templates.length > 0 && (
                <div>
                  <Label>Apply Template</Label>
                  <Select value={formData.template_id || 'none'} onValueChange={applyTemplate}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select template (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No template</SelectItem>
                      {templates.filter(t => t.template_id).map(t => (
                        <SelectItem key={t.template_id} value={t.template_id}>
                          {t.name} ({t.milestones?.length || 0} milestones)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Insert Template - For Existing Workflows */}
              {editingWorkflow && templates.length > 0 && (
                <div className="p-4 border-2 border-dashed rounded-lg space-y-3 bg-muted/30">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <FileInput className="w-4 h-4" />
                    Insert Template Milestones
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Add milestones from a template at a specific position
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Template</Label>
                      <Select value={insertTemplateId} onValueChange={setInsertTemplateId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select template" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select template...</SelectItem>
                          {templates.filter(t => t.template_id).map(t => (
                            <SelectItem key={t.template_id} value={t.template_id}>
                              {t.name} ({t.milestones?.length || 0})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Insert After</Label>
                      <Select value={insertAfterMilestone} onValueChange={setInsertAfterMilestone}>
                        <SelectTrigger>
                          <SelectValue placeholder="Position" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="start">At Start</SelectItem>
                          {formData.milestones.map((ms, idx) => (
                            <SelectItem key={ms.milestone_id || idx} value={ms.milestone_id || `idx_${idx}`}>
                              After: {ms.name} (Day {ms.due_days})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Days After</Label>
                      <Input
                        type="number"
                        value={insertDaysAfter}
                        onChange={(e) => setInsertDaysAfter(parseInt(e.target.value) || 1)}
                        min={1}
                        className="w-full"
                        placeholder="Days"
                      />
                    </div>
                  </div>
                  <Button 
                    type="button" 
                    variant="secondary"
                    onClick={handleInsertTemplate}
                    disabled={!insertTemplateId || insertTemplateId === 'none'}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Insert Template Milestones
                  </Button>
                </div>
              )}

              {/* Milestones */}
              <div>
                <Label className="text-base font-semibold">
                  Milestones ({formData.milestones.length})
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Due days: negative = before photo day, positive = after photo day
                </p>
                
                {/* Add Milestone Form */}
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Milestone name"
                    value={milestoneForm.name}
                    onChange={(e) => setMilestoneForm({...milestoneForm, name: e.target.value})}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Days"
                    value={milestoneForm.due_days}
                    onChange={(e) => setMilestoneForm({...milestoneForm, due_days: e.target.value})}
                    className="w-24"
                  />
                  <Button type="button" onClick={addMilestoneToWorkflow} size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Milestones List */}
                {formData.milestones.length > 0 && (
                  <div className="space-y-3 mt-3">
                    {formData.milestones
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((ms, idx) => (
                        <div key={ms.milestone_id || idx} className="p-3 bg-muted rounded-lg space-y-2">
                          {/* Main row */}
                          <div className="flex items-center gap-2">
                            <div className="flex flex-col gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5"
                                onClick={() => moveMilestone(idx, -1)}
                                disabled={idx === 0}
                              >
                                <ArrowUp className="w-3 h-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5"
                                onClick={() => moveMilestone(idx, 1)}
                                disabled={idx === formData.milestones.length - 1}
                              >
                                <ArrowDown className="w-3 h-3" />
                              </Button>
                            </div>
                            <span className="text-sm font-mono text-muted-foreground w-6">{idx + 1}.</span>
                            <Input
                              value={ms.name}
                              onChange={(e) => updateMilestoneInForm(idx, 'name', e.target.value)}
                              className="flex-1 h-8"
                              placeholder="Milestone name"
                            />
                            <Input
                              type="number"
                              value={ms.due_days ?? 0}
                              onChange={(e) => {
                                const val = e.target.value;
                                // Allow empty, negative sign, or valid numbers
                                if (val === '' || val === '-') {
                                  updateMilestoneInForm(idx, 'due_days', val);
                                } else {
                                  const num = parseInt(val, 10);
                                  if (!isNaN(num)) {
                                    updateMilestoneInForm(idx, 'due_days', num);
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                // Ensure we have a valid number on blur
                                const num = parseInt(e.target.value, 10);
                                updateMilestoneInForm(idx, 'due_days', isNaN(num) ? 0 : num);
                              }}
                              className="w-20 h-8"
                            />
                            <span className="text-xs text-muted-foreground w-8">days</span>
                            {editingWorkflow && (
                              <Select 
                                value={ms.status || 'pending'} 
                                onValueChange={(v) => updateMilestoneInForm(idx, 'status', v)}
                              >
                                <SelectTrigger className="w-28 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {milestoneStatuses.map(s => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => removeMilestoneFromForm(idx)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          {/* Notes row */}
                          <div className="ml-8 pl-6">
                            <DebouncedTextarea
                              value={ms.notes || ''}
                              onChange={(e) => updateMilestoneInForm(idx, 'notes', e.target.value)}
                              placeholder="Add notes for this milestone..."
                              className="text-sm min-h-[60px] resize-none"
                              rows={2}
                              debounceMs={300}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingWorkflow ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'New Template'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Template Name *</Label>
                  <Input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={templateForm.category} onValueChange={handleTemplateCategoryChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {workflowCategories.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(templateForm.category === 'production' || templateForm.category === 'prephotoday') && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {templateForm.category === 'production' 
                        ? 'First milestone: Photography Date'
                        : 'Confirmation Letter (-42), Photography Date (0), Dispatch Date (+42)'}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                  rows={2}
                />
              </div>

              <Separator />

              {/* Template Milestones */}
              <div>
                <Label className="text-base font-semibold">Milestones ({templateForm.milestones.length})</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Due days: negative = before photo day, positive = after photo day
                </p>
                
                {/* Add Milestone Form */}
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Milestone name"
                    value={milestoneForm.name}
                    onChange={(e) => setMilestoneForm({...milestoneForm, name: e.target.value})}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Days"
                    value={milestoneForm.due_days}
                    onChange={(e) => setMilestoneForm({...milestoneForm, due_days: e.target.value})}
                    className="w-24"
                  />
                  <Button type="button" onClick={addMilestoneToTemplate} size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Milestones List */}
                {templateForm.milestones.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {templateForm.milestones
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((ms, idx) => (
                        <div key={ms.milestone_id || idx} className="flex items-center gap-2 p-2 bg-muted rounded">
                          <div className="flex flex-col gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => moveTemplateMilestone(idx, -1)}
                              disabled={idx === 0}
                            >
                              <ArrowUp className="w-3 h-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => moveTemplateMilestone(idx, 1)}
                              disabled={idx === templateForm.milestones.length - 1}
                            >
                              <ArrowDown className="w-3 h-3" />
                            </Button>
                          </div>
                          <span className="text-sm font-mono text-muted-foreground w-6">{idx + 1}.</span>
                          <Input
                            value={ms.name}
                            onChange={(e) => updateMilestoneInTemplate(idx, 'name', e.target.value)}
                            className="flex-1 h-8"
                          />
                          <Input
                            type="number"
                            value={ms.due_days ?? 0}
                            onChange={(e) => {
                              const val = e.target.value;
                              // Allow empty, negative sign, or valid numbers
                              if (val === '' || val === '-') {
                                updateMilestoneInTemplate(idx, 'due_days', val);
                              } else {
                                const num = parseInt(val, 10);
                                if (!isNaN(num)) {
                                  updateMilestoneInTemplate(idx, 'due_days', num);
                                }
                              }
                            }}
                            onBlur={(e) => {
                              // Ensure we have a valid number on blur
                              const num = parseInt(e.target.value, 10);
                              updateMilestoneInTemplate(idx, 'due_days', isNaN(num) ? 0 : num);
                            }}
                            className="w-20 h-8"
                          />
                          <span className="text-xs text-muted-foreground w-8">days</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => removeMilestoneFromTemplate(idx)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingTemplate ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Milestone Completion Dialog */}
      <Dialog open={milestoneDialogOpen} onOpenChange={setMilestoneDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedMilestone?.status === 'completed' ? (
                <>
                  <Clock className="w-5 h-5 text-orange-500" />
                  Mark "{selectedMilestone?.name}" as Pending?
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Mark "{selectedMilestone?.name}" as Complete?
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Notes Section */}
            <div>
              <Label className="text-sm font-medium">Milestone Notes</Label>
              <DebouncedTextarea
                value={milestoneDialogNotes}
                onChange={(e) => setMilestoneDialogNotes(e.target.value)}
                placeholder="Add notes about this milestone..."
                className="mt-1.5 min-h-[100px]"
                rows={4}
                debounceMs={300}
              />
            </div>
            
            {/* Audit Log Section */}
            {milestoneAuditLogs.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Activity Log</Label>
                <div className="max-h-40 overflow-y-auto space-y-2 bg-muted/50 rounded-lg p-3">
                  {milestoneAuditLogs.map((log) => (
                    <div key={log.audit_id} className="text-xs border-b border-border/50 pb-2 last:border-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {log.old_status} → {log.new_status}
                        </span>
                        <span className="text-muted-foreground">
                          {new Date(log.changed_at).toLocaleDateString('en-AU', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'Australia/Sydney'
                          })}
                        </span>
                      </div>
                      <p className="text-muted-foreground">
                        by {log.changed_by_name || 'Unknown'}
                      </p>
                      {log.notes && (
                        <p className="text-muted-foreground italic mt-1">"{log.notes}"</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {milestoneAuditLogs.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">
                No previous activity for this milestone
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMilestoneDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmMilestoneToggle}
              disabled={milestoneDialogLoading}
              className={selectedMilestone?.status === 'completed' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'}
            >
              {milestoneDialogLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {selectedMilestone?.status === 'completed' ? 'Mark as Pending' : 'Mark as Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workflow Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Workflow
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>Are you sure you want to delete this workflow? This action cannot be undone.</p>
              
              {workflowToDelete && (
                <div className="bg-muted p-4 rounded-lg text-left space-y-2">
                  <p className="font-semibold text-foreground">{workflowToDelete.name}</p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Organisation:</span>{' '}
                    {organizations.find(o => o.org_id === workflowToDelete.org_id)?.name || 'Unknown'}
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Category:</span>{' '}
                    <Badge variant="outline" className="ml-1">{workflowToDelete.category}</Badge>
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Milestones:</span>{' '}
                    {workflowToDelete.milestones?.length || 0} milestone(s)
                  </p>
                  {workflowToDelete.notes && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Notes:</span>{' '}
                      {workflowToDelete.notes.substring(0, 100)}{workflowToDelete.notes.length > 100 ? '...' : ''}
                    </p>
                  )}
                </div>
              )}
              
              <p className="text-sm text-muted-foreground italic">
                A notification email with the workflow details will be sent to you for your records.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Workflow'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Photography Date Prompt Dialog for PrePhotoDay */}
      <Dialog open={photoDatePromptOpen} onOpenChange={setPhotoDatePromptOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              Photography Date Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              No photography date is set for this organisation for {formData.year}. 
              Please enter the photography date to create a Schedule workflow.
            </p>
            <div>
              <Label>Photography Date *</Label>
              <Input
                type="date"
                value={pendingPhotoDate}
                onChange={(e) => setPendingPhotoDate(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This date will be saved to the organisation and used as the completion date.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setPhotoDatePromptOpen(false);
              setFormData(prev => ({ ...prev, category: 'production' })); // Reset to production
            }}>
              Cancel
            </Button>
            <Button onClick={handleSavePhotoDate} disabled={!pendingPhotoDate}>
              Save & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
